import { apiClient } from './api/client';
import {
  PublicFormDataApiDto,
  PublicFormDataAndVersionApiDto,
  VersionApiDto,
  GuestAndFormVersionApiDto,
  VehicleAndFormVersionApiDto,
  PetAndFormVersionApiDto,
  PictureUploadApiDto,
  PictureUploadWithGuestNumIdApiDto,
  GuestPictureIdApiDto,
  ResultApiDto,
  PersonIdApiDto,
  ContractApiDto,
  ContractSignatureApiDto,
  PresignedUrlRequestApiDto,
  PresignedUrlApiDto,
  GuestImageRegistrationApiDto,
  ContentTypeApiDto,
  IdScanUploadApiDto,
  IdScanApiDto,
  IdScanResultApiDto
} from '@/types/api';

class FormService {
  // GET /v1/public/forms/{publicId}
  public async getForm(publicId: string, signal?: AbortSignal): Promise<PublicFormDataApiDto> {
    return apiClient.get<PublicFormDataApiDto>(`/public/forms/${publicId}`, undefined, undefined, signal);
  }

  // DEPRECATED: PUT /v1/public/forms/{publicId} (update form's data)
  public async updateFormData(
    publicId: string,
    request: PublicFormDataAndVersionApiDto
  ): Promise<VersionApiDto> {
    return apiClient.put<VersionApiDto>(`/public/forms/${publicId}`, request);
  }

  // PUT /v1/public/forms/{publicId}/guest/{guestNumId}
  public async updateGuestData(
    publicId: string,
    guestNumId: number,
    request: GuestAndFormVersionApiDto
  ): Promise<PublicFormDataAndVersionApiDto> {
    return apiClient.put<PublicFormDataAndVersionApiDto>(
      `/public/forms/${publicId}/guest/${guestNumId}`,
      request
    );
  }

  // PUT /v1/public/forms/{publicId}/vehicle/{vehicleNumId}
  public async updateVehicleData(
    publicId: string,
    vehicleNumId: number,
    request: VehicleAndFormVersionApiDto
  ): Promise<PublicFormDataAndVersionApiDto> {
    return apiClient.put<PublicFormDataAndVersionApiDto>(
      `/public/forms/${publicId}/vehicle/${vehicleNumId}`,
      request
    );
  }

  // PUT /v1/public/forms/{publicId}/pet/{petNumId}
  public async updatePetData(
    publicId: string,
    petNumId: number,
    request: PetAndFormVersionApiDto
  ): Promise<PublicFormDataAndVersionApiDto> {
    return apiClient.put<PublicFormDataAndVersionApiDto>(
      `/public/forms/${publicId}/pet/${petNumId}`,
      request
    );
  }

  // DEPRECATED: POST /v1/public/forms/{publicId}/guest/{guestNumId}/picture
  // Use getPresignedUploadUrl + uploadToS3 + registerGuestImage instead
  public async uploadGuestPicture(
    publicId: string,
    guestNumId: number,
    request: PictureUploadApiDto
  ): Promise<GuestPictureIdApiDto> {
    return apiClient.post<GuestPictureIdApiDto>(
      `/public/forms/${publicId}/guest/${guestNumId}/picture`,
      request
    );
  }

  // NEW: POST /v1/public/forms/{publicId}/guest/{guestNumId}/picture/presign
  public async getPresignedUploadUrl(
    publicId: string,
    guestNumId: number,
    request: PresignedUrlRequestApiDto
  ): Promise<PresignedUrlApiDto> {
    return apiClient.post<PresignedUrlApiDto>(
      `/public/forms/${publicId}/guest/${guestNumId}/picture/presign`,
      request
    );
  }

  // NEW: POST /v1/public/forms/{publicId}/guest/{guestNumId}/picture/register
  public async registerGuestImage(
    publicId: string,
    guestNumId: number,
    request: GuestImageRegistrationApiDto
  ): Promise<GuestPictureIdApiDto> {
    return apiClient.post<GuestPictureIdApiDto>(
      `/public/forms/${publicId}/guest/${guestNumId}/picture/register`,
      request
    );
  }

  // DEPRECATED: POST /v1/public/forms/{publicId}/picture
  public async uploadPicture(
    publicId: string,
    request: PictureUploadWithGuestNumIdApiDto
  ): Promise<GuestPictureIdApiDto> {
    return apiClient.post<GuestPictureIdApiDto>(
      `/public/forms/${publicId}/picture`,
      request
    );
  }

  // DEPRECATED: DELETE /v1/public/forms/{publicId}/picture?type=...&guestNumId=...
  public async removePicture(
    publicId: string,
    type: string,
    guestNumId: number
  ): Promise<void> {
    return apiClient.delete<void>(
      `/public/forms/${publicId}/picture?type=${type}&guestNumId=${guestNumId}`
    );
  }

  // DEPRECATED: DELETE /v1/public/forms/{publicId}/guest/{guestNumId}/picture?type=...
  public async removeGuestPicture(
    publicId: string,
    guestNumId: number,
    type: string
  ): Promise<void> {
    return apiClient.delete<void>(
      `/public/forms/${publicId}/guest/${guestNumId}/picture?type=${type}`
    );
  }

  // Entry-based file endpoints (resolve guestNumId server-side)

  public async getPresignedUploadUrlByEntry(
    publicId: string,
    entryId: string,
    request: PresignedUrlRequestApiDto
  ): Promise<PresignedUrlApiDto> {
    return apiClient.post<PresignedUrlApiDto>(
      `/public/forms/${publicId}/guests/${entryId}/picture/presign`,
      request
    );
  }

  public async registerGuestImageByEntry(
    publicId: string,
    entryId: string,
    request: GuestImageRegistrationApiDto
  ): Promise<GuestPictureIdApiDto> {
    return apiClient.post<GuestPictureIdApiDto>(
      `/public/forms/${publicId}/guests/${entryId}/picture/register`,
      request
    );
  }

  public async removeGuestPictureByEntry(
    publicId: string,
    entryId: string,
    type: string
  ): Promise<void> {
    return apiClient.delete<void>(
      `/public/forms/${publicId}/guests/${entryId}/picture?type=${type}`
    );
  }

  // POST /v1/public/forms/{publicId}/is-latest
  public async isLatestFormDataVersion(
    publicId: string,
    request: VersionApiDto,
    signal?: AbortSignal
  ): Promise<ResultApiDto> {
    return apiClient.post<ResultApiDto>(`/public/forms/${publicId}/is-latest`, request, undefined, signal);
  }

  // DEPRECATED: PUT /v1/public/forms/{publicId}/save-and-complete
  public async updatePublicFormCompleted(
    publicId: string,
    request: PublicFormDataAndVersionApiDto
  ): Promise<void> {
    await apiClient.put<void>(`/public/forms/${publicId}/save-and-complete`, request);
  }

  // PUT /v1/public/forms/{publicId}/complete?lang=
  //
  // One of the two calls that still sends the locale. The server persists it as the form's
  // submittedLocale and reads it back to decide which language the guest's confirmation email is
  // written in — so dropping `lang` here would not fail, it would quietly send English mail to a
  // German guest.
  public async completeForm(
    publicId: string,
    request: VersionApiDto
  ): Promise<void> {
    await apiClient.put<void>(apiClient.withLang(`/public/forms/${publicId}/complete`), request);
  }

  // POST /v1/public/verify-id
  public async verifyId(request: PersonIdApiDto): Promise<ResultApiDto> {
    return apiClient.post<ResultApiDto>(`/public/verify-id`, request);
  }

  // NEW: POST /v1/public/forms/{publicId}/vehicle (create vehicle)
  public async createVehicle(
    publicId: string,
    request: VehicleAndFormVersionApiDto
  ): Promise<PublicFormDataAndVersionApiDto> {
    return apiClient.post<PublicFormDataAndVersionApiDto>(`/public/forms/${publicId}/vehicle`, request);
  }

  // NEW: DELETE /v1/public/forms/{publicId}/vehicle/{vehicleNumId}?version=...
  public async deleteVehicle(
    publicId: string,
    vehicleNumId: number,
    version: string
  ): Promise<PublicFormDataAndVersionApiDto> {
    return apiClient.delete<PublicFormDataAndVersionApiDto>(
      `/public/forms/${publicId}/vehicle/${vehicleNumId}?version=${version}`
    );
  }

  // NEW: POST /v1/public/forms/{publicId}/pet (create pet)
  public async createPet(
    publicId: string,
    request: PetAndFormVersionApiDto
  ): Promise<PublicFormDataAndVersionApiDto> {
    return apiClient.post<PublicFormDataAndVersionApiDto>(`/public/forms/${publicId}/pet`, request);
  }

  // NEW: DELETE /v1/public/forms/{publicId}/pet/{petNumId}?version=...
  public async deletePet(
    publicId: string,
    petNumId: number,
    version: string
  ): Promise<PublicFormDataAndVersionApiDto> {
    return apiClient.delete<PublicFormDataAndVersionApiDto>(
      `/public/forms/${publicId}/pet/${petNumId}?version=${version}`
    );
  }

  // NEW: DELETE /v1/public/forms/{publicId}/guest/{guestNumId}?version=...
  public async removeGuest(
    publicId: string,
    guestNumId: number,
    version: string
  ): Promise<void> {
    return apiClient.delete<void>(`/public/forms/${publicId}/guest/${guestNumId}?version=${version}`);
  }

  // === NEW RELATIONAL GUEST ENTRY ENDPOINTS (Story 6-1) ===

  // GET /v1/public/forms/{publicId}/guests
  public async getGuestEntries(publicId: string, signal?: AbortSignal): Promise<import('@/types/guest').GuestEntryListResponse> {
    return apiClient.get<import('@/types/guest').GuestEntryListResponse>(`/public/forms/${publicId}/guests`, undefined, undefined, signal);
  }

  // POST /v1/public/forms/{publicId}/guests
  public async createGuestEntry(
    publicId: string,
    request: import('@/types/guest').CreateGuestEntryRequest
  ): Promise<import('@/types/guest').FormGuestEntry> {
    return apiClient.post<import('@/types/guest').FormGuestEntry>(`/public/forms/${publicId}/guests`, request);
  }

  // PUT /v1/public/forms/{publicId}/guests/{entryId}
  public async updateGuestEntry(
    publicId: string,
    entryId: string,
    request: import('@/types/guest').UpdateGuestEntryRequest
  ): Promise<import('@/types/guest').FormGuestEntry> {
    return apiClient.put<import('@/types/guest').FormGuestEntry>(`/public/forms/${publicId}/guests/${entryId}`, request);
  }

  // GET /v1/public/forms/{publicId}/guests/count
  public async getGuestCount(publicId: string): Promise<{ currentCount: number; declaredLimit: number; spotsRemaining: number }> {
    return apiClient.get<{ currentCount: number; declaredLimit: number; spotsRemaining: number }>(`/public/forms/${publicId}/guests/count`);
  }

  // DELETE /v1/public/forms/{publicId}/guests/{entryId}
  public async deleteGuestEntry(publicId: string, entryId: string): Promise<void> {
    return apiClient.delete<void>(`/public/forms/${publicId}/guests/${entryId}`);
  }

  // PUT /v1/public/forms/{publicId}/guests/{entryId}/designate-main
  public async designateMainGuest(
    publicId: string,
    entryId: string
  ): Promise<import('@/types/guest').FormGuestEntry> {
    return apiClient.put<import('@/types/guest').FormGuestEntry>(`/public/forms/${publicId}/guests/${entryId}/designate-main`, {});
  }

  // Convenience wrapper for autosave (saves form data as stringified JSON)
  public async saveForm(
    publicId: string,
    request: { data: string; version: string }
  ): Promise<void> {
    await apiClient.put<void>(`/public/forms/${publicId}/save`, request);
  }

  // GET /v1/public/forms/{publicId}/contract
  public async getContract(publicId: string, signal?: AbortSignal): Promise<ContractApiDto> {
    return apiClient.get<ContractApiDto>(`/public/forms/${publicId}/contract`, undefined, undefined, signal);
  }

  // NEW: POST /v1/public/forms/{publicId}/contract/sign/presign
  public async getContractSignPresignedUrl(
    publicId: string,
    request: ContentTypeApiDto
  ): Promise<PresignedUrlApiDto> {
    return apiClient.post<PresignedUrlApiDto>(
      `/public/forms/${publicId}/contract/sign/presign`,
      request
    );
  }

  // POST /v1/public/forms/{publicId}/id-scans
  // Story 42-15: starts (or serves from cache) the ID photo check for an uploaded ID_FRONT/ID_BACK.
  // The signal is the check's one deadline, shared with the poll below.
  public async createIdScan(
    publicId: string,
    request: IdScanUploadApiDto,
    signal?: AbortSignal
  ): Promise<IdScanApiDto> {
    return apiClient.post<IdScanApiDto>(`/public/forms/${publicId}/id-scans`, request, undefined, signal);
  }

  // GET /v1/public/forms/{publicId}/id-scans/{scanId}
  // Story 42-15: one poll of the check. Passing the signal also opts this GET out of the in-flight
  // merge in apiClient.get, so every poll is a fresh request.
  public async getIdScan(
    publicId: string,
    scanId: string,
    signal?: AbortSignal
  ): Promise<IdScanResultApiDto> {
    return apiClient.get<IdScanResultApiDto>(
      `/public/forms/${publicId}/id-scans/${scanId}`,
      undefined,
      undefined,
      signal
    );
  }

  // POST /v1/public/forms/{publicId}/contract/sign/register
  public async signContract(
    publicId: string,
    request: ContractSignatureApiDto
  ): Promise<void> {
    return apiClient.post<void>(`/public/forms/${publicId}/contract/sign/register`, request);
  }
}

export const formService = new FormService();
